import { locales, type Locale, type TranslationResource } from './types'

// Utility functions for i18n management

/**
 * Deep merge translation objects, with source overriding target
 */
export function mergeTranslations(
  target: TranslationResource,
  source: TranslationResource
): TranslationResource {
  const result = { ...target }

  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      if (
        typeof source[key] === 'object' && 
        source[key] !== null && 
        !Array.isArray(source[key]) &&
        typeof target[key] === 'object' &&
        target[key] !== null &&
        !Array.isArray(target[key])
      ) {
        result[key] = mergeTranslations(
          target[key] as TranslationResource,
          source[key] as TranslationResource
        )
      } else {
        result[key] = source[key]
      }
    }
  }

  return result
}

/**
 * Get nested translation value by dot notation key
 */
export function getNestedValue(obj: TranslationResource, path: string): string {
  const keys = path.split('.')
  let current: any = obj

  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key]
    } else {
      return path // Return key as fallback
    }
  }

  return typeof current === 'string' ? current : path
}

/**
 * Validate that translation object has all required keys from schema
 */
export function validateTranslations(
  translations: TranslationResource,
  schema: TranslationResource,
  path = ''
): string[] {
  const missing: string[] = []

  for (const key in schema) {
    const currentPath = path ? `${path}.${key}` : key
    
    if (!(key in translations)) {
      missing.push(currentPath)
    } else if (
      typeof schema[key] === 'object' && 
      schema[key] !== null &&
      typeof translations[key] === 'object' &&
      translations[key] !== null
    ) {
      missing.push(
        ...validateTranslations(
          translations[key] as TranslationResource,
          schema[key] as TranslationResource,
          currentPath
        )
      )
    }
  }

  return missing
}

/**
 * Extract all translation keys from a translation object
 */
export function extractTranslationKeys(
  obj: TranslationResource,
  prefix = ''
): string[] {
  const keys: string[] = []

  for (const key in obj) {
    const fullKey = prefix ? `${prefix}.${key}` : key
    
    if (typeof obj[key] === 'string') {
      keys.push(fullKey)
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      keys.push(...extractTranslationKeys(obj[key] as TranslationResource, fullKey))
    }
  }

  return keys
}

/**
 * Create a translation function that supports dot notation
 */
export function createTranslationFunction(
  translations: Record<Locale, TranslationResource>,
  locale: Locale,
  fallbackLocale: Locale = 'en'
): (key: string, options?: { fallback?: string }) => string {
  return (key: string, options?: { fallback?: string }) => {
    // Try current locale first
    let value = getNestedValue(translations[locale] || {}, key)
    
    // If not found and not the fallback key, try fallback locale
    if (value === key && locale !== fallbackLocale) {
      value = getNestedValue(translations[fallbackLocale] || {}, key)
    }
    
    // If still not found, use custom fallback or return key
    if (value === key && options?.fallback) {
      return options.fallback
    }
    
    return value
  }
}

/**
 * Check if all required locales are present in translations
 */
export function validateLocales(
  translations: Record<string, TranslationResource>,
  requiredLocales: Locale[]
): string[] {
  return requiredLocales.filter(locale => !(locale in translations))
}

/**
 * Get missing translations between two locale objects
 */
export function getMissingTranslations(
  sourceTranslations: TranslationResource,
  targetTranslations: TranslationResource
): string[] {
  const sourceKeys = extractTranslationKeys(sourceTranslations)
  const targetKeys = extractTranslationKeys(targetTranslations)
  
  return sourceKeys.filter(key => !targetKeys.includes(key))
}

/**
 * Create a namespace wrapper for translations
 */
export function createNamespacedTranslations<T extends TranslationResource>(
  namespace: string,
  translations: Record<Locale, T>
): Record<Locale, Record<string, T>> {
  return locales.reduce((result, locale) => {
    result[locale] = {
      [namespace]: translations[locale]
    }
    return result
  }, {} as Record<Locale, Record<string, T>>)
}
